// src/lib/realtime/RealtimeManager.ts
// Centralized Realtime Manager - Single source of truth for all Supabase realtime connections

import { RealtimeChannel, SupabaseClient } from '@supabase/supabase-js'
import { QueryClient } from '@tanstack/react-query'

type ConnectionState = 'connected' | 'reconnecting' | 'disconnected'
type StateChangeCallback = (state: ConnectionState) => void

interface JobDetailsSubscription {
  channel: RealtimeChannel
  unsubscribe: () => void
}

// Estimate events fired when realtime payloads arrive. `status_change` is
// detected by comparing newRecord.status against the row we already had in
// cache, which avoids a hard dependency on REPLICA IDENTITY FULL for the
// estimates table.
export type EstimateRealtimeEvent =
  | {
      type: 'status_change'
      estimateId: string
      oldStatus: string | undefined
      newStatus: string
      estimate: any
    }
  | { type: 'update'; estimateId: string; newRecord: any }
  | { type: 'create'; estimateId: string; newRecord: any }
  | { type: 'delete'; estimateId: string }

type EstimateEventListener = (event: EstimateRealtimeEvent) => void

// Invoice events mirror the estimate event shape. Status-change uses the
// same cache-based prev-status capture so we don't depend on REPLICA
// IDENTITY FULL (though the invoices table happens to have it).
export type InvoiceRealtimeEvent =
  | {
      type: 'status_change'
      invoiceId: string
      oldStatus: string | undefined
      newStatus: string
      invoice: any
    }
  | { type: 'update'; invoiceId: string; newRecord: any }
  | { type: 'create'; invoiceId: string; newRecord: any }
  | { type: 'delete'; invoiceId: string }

type InvoiceEventListener = (event: InvoiceRealtimeEvent) => void

// Job events follow the same shape as estimates/invoices. status_change
// detection compares newRecord.status against the pre-patch cached row,
// which avoids depending on REPLICA IDENTITY FULL on the jobs table
// (jobs is partitioned and the replica identity defaults to the partition
// primary key — old.status would be missing on UPDATE payloads).
export type JobRealtimeEvent =
  | {
      type: 'status_change'
      jobId: string
      oldStatus: string | undefined
      newStatus: string
      job: any
    }
  | { type: 'update'; jobId: string; newRecord: any }
  | { type: 'create'; jobId: string; newRecord: any }
  | { type: 'delete'; jobId: string }

type JobEventListener = (event: JobRealtimeEvent) => void

class RealtimeManagerClass {
  private supabase: SupabaseClient | null = null
  private queryClient: QueryClient | null = null
  private currentCompanyId: string | null = null
  
  // Subscription channels
  private jobsChannel: RealtimeChannel | null = null
  private estimatesChannel: RealtimeChannel | null = null
  private invoicesChannel: RealtimeChannel | null = null
  private userAccessChannel: RealtimeChannel | null = null
  private userAccessUserId: string | null = null
  private userAccessDebounceTimer: ReturnType<typeof setTimeout> | null = null
  private jobDetailsChannels: Map<string, JobDetailsSubscription> = new Map()

  // Connection state
  private connectionState: ConnectionState = 'disconnected'
  private stateChangeListeners: Set<StateChangeCallback> = new Set()
  private estimateEventListeners: Set<EstimateEventListener> = new Set()
  private invoiceEventListeners: Set<InvoiceEventListener> = new Set()
  private jobEventListeners: Set<JobEventListener> = new Set()
  
  // Lifecycle management
  private isInitialized = false
  private visibilityDebounceTimer: ReturnType<typeof setTimeout> | null = null
  private reconnectAttempts = 0
  private maxReconnectAttempts = 3
  private reconnectBackoffMs = 2000
  
  // Event listener references for cleanup
  private boundHandleVisibilityChange: (() => void) | null = null
  private boundHandleOnline: (() => void) | null = null
  private boundHandleFocus: (() => void) | null = null

  /**
   * Initialize the RealtimeManager with Supabase client and QueryClient
   */
  initialize(supabase: SupabaseClient, companyId: string, queryClient: QueryClient): void {
    // Clean up old company subscriptions if switching companies
    if (this.currentCompanyId && this.currentCompanyId !== companyId) {
      console.log('[RealtimeManager] Company changed, cleaning up old subscriptions')
      this.destroy()
    }
    
    // Skip if already initialized for this company
    if (this.isInitialized && this.currentCompanyId === companyId) {
      console.log('[RealtimeManager] Already initialized for company:', companyId)
      return
    }
    
    console.log('[RealtimeManager] Initializing for company:', companyId)
    
    this.supabase = supabase
    this.currentCompanyId = companyId
    this.queryClient = queryClient
    this.isInitialized = true
    this.reconnectAttempts = 0
    
    // Setup lifecycle event listeners
    this.setupLifecycleListeners()
    
    // Start the always-on jobs subscription
    this.setupJobsSubscription()

    // Start the always-on estimates subscription. Estimates live on the
    // per-entity React Query cache (lib/cache/estimates.js, key
    // `['estimates', companyId]`) and need realtime to drive client-status
    // toasts and to keep an open estimate form in sync when the recipient
    // accepts/declines on the public view.
    this.setupEstimatesSubscription()

    // Start the always-on invoices subscription. Same shape as estimates —
    // per-entity cache at `['invoices', companyId]`, status-change toasts,
    // keeps the Invoices view in sync when a client pays via the magic link.
    this.setupInvoicesSubscription()
  }

  /**
   * Setup visibility, focus, and online event listeners
   */
  private setupLifecycleListeners(): void {
    // Cleanup any existing listeners first
    this.cleanupLifecycleListeners()
    
    this.boundHandleVisibilityChange = this.handleVisibilityChange.bind(this)
    this.boundHandleOnline = this.handleOnline.bind(this)
    this.boundHandleFocus = this.handleFocus.bind(this)
    
    document.addEventListener('visibilitychange', this.boundHandleVisibilityChange)
    window.addEventListener('online', this.boundHandleOnline)
    window.addEventListener('focus', this.boundHandleFocus)
    
    console.log('[RealtimeManager] Lifecycle listeners attached')
  }

  /**
   * Cleanup lifecycle event listeners
   */
  private cleanupLifecycleListeners(): void {
    if (this.boundHandleVisibilityChange) {
      document.removeEventListener('visibilitychange', this.boundHandleVisibilityChange)
    }
    if (this.boundHandleOnline) {
      window.removeEventListener('online', this.boundHandleOnline)
    }
    if (this.boundHandleFocus) {
      window.removeEventListener('focus', this.boundHandleFocus)
    }
    
    if (this.visibilityDebounceTimer) {
      clearTimeout(this.visibilityDebounceTimer)
      this.visibilityDebounceTimer = null
    }
  }

  /**
   * Handle visibility change (tab hidden/visible)
   */
  private handleVisibilityChange(): void {
    if (document.hidden) {
      console.log('[RealtimeManager] App hidden')
      return
    }
    
    console.log('[RealtimeManager] App visible, checking connection health')
    
    // Debounce to handle rapid visibility toggling
    if (this.visibilityDebounceTimer) {
      clearTimeout(this.visibilityDebounceTimer)
    }
    
    this.visibilityDebounceTimer = setTimeout(() => {
      this.visibilityDebounceTimer = null
      this.handleWakeUp()
    }, 300)
  }

  /**
   * Handle network coming online
   */
  private handleOnline(): void {
    console.log('[RealtimeManager] Network online, checking connection')
    this.handleWakeUp()
  }

  /**
   * Handle window focus (belt-and-suspenders check)
   */
  private handleFocus(): void {
    // Lightweight health check only, don't spam reconnects
    if (this.connectionState === 'disconnected' && this.isInitialized) {
      console.log('[RealtimeManager] Focus detected while disconnected, triggering health check')
      this.checkHealth()
    }
  }

  /**
   * Handle wake up from background/offline
   */
  private async handleWakeUp(): Promise<void> {
    if (!this.isInitialized || !this.queryClient || !this.currentCompanyId) {
      return
    }

    // Defensive session refresh on visibility-back. supabase-js's auto-refresh
    // POST can be left in a stuck-pending state after a tab-hide (Chrome
    // doesn't always resume hidden-tab fetches); future getSession() calls
    // then await that stuck promise and block every REST request. Kicking off
    // an explicit refreshSession (raced against 5s) either replaces the stuck
    // state with a fresh request or surfaces an error instead of an infinite
    // hang. Fire-and-forget — we don't want to delay cache invalidation.
    if (this.supabase) {
      Promise.race([
        this.supabase.auth.refreshSession(),
        new Promise((_, reject) => setTimeout(
          () => reject(new Error('refreshSession timeout on wake')),
          5000
        ))
      ]).then(
        () => console.log('[RealtimeManager] Session refreshed on wake'),
        (err) => console.warn('[RealtimeManager] Wake-up refreshSession failed:', err?.message || err)
      )
    }

    const isHealthy = this.checkHealth()

    if (isHealthy) {
      // Connection is healthy, mark caches stale but DO NOT force an immediate
      // parallel refetch. The previous behaviour kicked off N parallel refetches
      // that could serialize behind supabase-js's auth Web Lock; any mutation
      // (e.g. Schedule Estimate) firing during that storm then hung in
      // useMutation.onMutate -> queryClient.cancelQueries, because the in-flight
      // fetches it tried to cancel were themselves stuck on the lock. By using
      // refetchType: 'none', the next consumer that mounts/reads triggers a
      // fresh fetch on demand — without piling on a thundering herd at wake.
      console.log('[RealtimeManager] Connection healthy, marking queries stale (lazy refetch)')
      const opts = { refetchType: 'none' as const }
      this.queryClient.invalidateQueries({ queryKey: ['allCompanyData', this.currentCompanyId] }, opts)
      this.queryClient.invalidateQueries({ queryKey: ['jobs', this.currentCompanyId] }, opts)
      this.queryClient.invalidateQueries({ queryKey: ['estimates', this.currentCompanyId] }, opts)
      this.queryClient.invalidateQueries({ queryKey: ['invoices', this.currentCompanyId] }, opts)
      this.queryClient.invalidateQueries({ queryKey: ['clients', this.currentCompanyId] }, opts)
      this.queryClient.invalidateQueries({ queryKey: ['calendar_events', this.currentCompanyId] }, opts)
      if (this.userAccessUserId) {
        this.queryClient.invalidateQueries({ queryKey: ['userAccessCache', this.userAccessUserId] }, opts)
      }
    } else {
      // Connection is unhealthy, trigger reconnect
      console.log('[RealtimeManager] Connection unhealthy, triggering reconnect')
      await this.reconnect()
    }
  }

  /**
   * Setup the always-on jobs subscription
   */
  private setupJobsSubscription(): void {
    if (!this.supabase || !this.currentCompanyId || !this.queryClient) {
      console.error('[RealtimeManager] Cannot setup jobs subscription: missing dependencies')
      return
    }
    
    // Cleanup existing jobs channel
    if (this.jobsChannel) {
      this.supabase.removeChannel(this.jobsChannel)
      this.jobsChannel = null
    }
    
    const companyId = this.currentCompanyId
    const queryClient = this.queryClient
    
    console.log('[RealtimeManager] Setting up jobs subscription for company:', companyId)
    
    this.jobsChannel = this.supabase
      .channel(`jobs_${companyId}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'jobs',
        filter: `company_id=eq.${companyId}`
      }, (payload) => {
        console.log('[RealtimeManager] Jobs update received:', payload.eventType)
        this.handleJobsUpdate(payload, companyId, queryClient)
      })
      .subscribe((status, err) => {
        this.handleSubscriptionStatus('jobs', status, err)
      })
  }

  /**
   * Handle jobs table updates. Writes to the per-entity cache key
   * `['jobs', companyId]` (NOT the legacy mega-query) so the patch lands on
   * the same cache useJobsQuery reads.
   *
   * Dedupe rules match estimates/invoices:
   *   INSERT — skip if id already present. Covers optimistic-create race
   *     against onSuccess reconcile (the optimistic placeholder has a
   *     tempId, but reconcile may have already replaced it by the time the
   *     realtime fanout arrives) AND multi-tab inserts.
   *   UPDATE — merge by id; if the row isn't in cache yet, append it.
   *   DELETE — filter by id.
   *
   * NOTE: jobs is a partitioned table. The supabase_realtime publication
   * has publish_via_partition_root = true (see migration
   * 20260616_enable_pubviaroot_for_partitioned_realtime) so payloads emit
   * with table='jobs' rather than table='jobs_2026_06'. Without that flag
   * this handler would never fire.
   */
  private handleJobsUpdate(
    payload: any,
    companyId: string,
    queryClient: QueryClient
  ): void {
    const { eventType, new: newRecord, old: oldRecord } = payload
    const key = ['jobs', companyId]

    let prevStatus: string | undefined

    queryClient.setQueryData(key, (old: any) => {
      const list: any[] = Array.isArray(old) ? old : []

      switch (eventType) {
        case 'INSERT': {
          if (list.some((j) => j?.id === newRecord.id)) return list
          return [newRecord, ...list]
        }
        case 'UPDATE': {
          const existing = list.find((j) => j?.id === newRecord.id)
          prevStatus = existing?.status
          if (!existing) return [newRecord, ...list]
          return list.map((j) =>
            j?.id === newRecord.id ? { ...j, ...newRecord } : j
          )
        }
        case 'DELETE': {
          return list.filter((j) => j?.id !== oldRecord.id)
        }
        default:
          return list
      }
    })

    if (eventType === 'INSERT') {
      this.emitJobEvent({
        type: 'create',
        jobId: newRecord.id,
        newRecord,
      })
    } else if (eventType === 'UPDATE') {
      this.emitJobEvent({
        type: 'update',
        jobId: newRecord.id,
        newRecord,
      })
      if (prevStatus !== undefined && prevStatus !== newRecord.status) {
        this.emitJobEvent({
          type: 'status_change',
          jobId: newRecord.id,
          oldStatus: prevStatus,
          newStatus: newRecord.status,
          job: newRecord,
        })
      }
    } else if (eventType === 'DELETE') {
      this.emitJobEvent({
        type: 'delete',
        jobId: oldRecord.id,
      })
    }
  }

  /**
   * Setup the always-on estimates subscription
   */
  private setupEstimatesSubscription(): void {
    if (!this.supabase || !this.currentCompanyId || !this.queryClient) {
      console.error('[RealtimeManager] Cannot setup estimates subscription: missing dependencies')
      return
    }

    if (this.estimatesChannel) {
      this.supabase.removeChannel(this.estimatesChannel)
      this.estimatesChannel = null
    }

    const companyId = this.currentCompanyId
    const queryClient = this.queryClient

    console.log('[RealtimeManager] Setting up estimates subscription for company:', companyId)

    this.estimatesChannel = this.supabase
      .channel(`estimates_${companyId}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'estimates',
        filter: `company_id=eq.${companyId}`
      }, (payload) => {
        console.log('[RealtimeManager] Estimates update received:', payload.eventType)
        this.handleEstimatesUpdate(payload, companyId, queryClient)
      })
      .subscribe((status, err) => {
        this.handleSubscriptionStatus('estimates', status, err)
      })
  }

  /**
   * Handle estimates table updates. Writes to the per-entity cache key
   * `['estimates', companyId]` (NOT the legacy mega-query) so the patch lands
   * on the same cache useEstimatesQuery reads.
   *
   * Dedupe rules:
   *   INSERT — skip if id already present. Covers the "I just created this
   *     row optimistically" race against onSuccess reconcile (the optimistic
   *     placeholder has a tempId, but reconcile may have already replaced it
   *     by the time the realtime fanout arrives) AND multi-tab inserts.
   *   UPDATE — merge by id; if the row isn't in cache yet, append it.
   *   DELETE — filter by id.
   *
   * Status-change detection compares newRecord.status against the cached
   * row's status BEFORE the patch is applied — that's why prevStatus is
   * captured inside the setQueryData callback. This avoids a hard dependency
   * on REPLICA IDENTITY FULL for the estimates table.
   */
  private handleEstimatesUpdate(
    payload: any,
    companyId: string,
    queryClient: QueryClient
  ): void {
    const { eventType, new: newRecord, old: oldRecord } = payload
    const key = ['estimates', companyId]

    let prevStatus: string | undefined

    queryClient.setQueryData(key, (old: any) => {
      const list: any[] = Array.isArray(old) ? old : []

      switch (eventType) {
        case 'INSERT': {
          if (list.some((e) => e?.id === newRecord.id)) return list
          return [newRecord, ...list]
        }
        case 'UPDATE': {
          const existing = list.find((e) => e?.id === newRecord.id)
          prevStatus = existing?.status
          if (!existing) return [newRecord, ...list]
          return list.map((e) =>
            e?.id === newRecord.id ? { ...e, ...newRecord } : e
          )
        }
        case 'DELETE': {
          return list.filter((e) => e?.id !== oldRecord.id)
        }
        default:
          return list
      }
    })

    if (eventType === 'INSERT') {
      this.emitEstimateEvent({
        type: 'create',
        estimateId: newRecord.id,
        newRecord,
      })
    } else if (eventType === 'UPDATE') {
      this.emitEstimateEvent({
        type: 'update',
        estimateId: newRecord.id,
        newRecord,
      })
      if (prevStatus !== undefined && prevStatus !== newRecord.status) {
        this.emitEstimateEvent({
          type: 'status_change',
          estimateId: newRecord.id,
          oldStatus: prevStatus,
          newStatus: newRecord.status,
          estimate: newRecord,
        })
      }
    } else if (eventType === 'DELETE') {
      this.emitEstimateEvent({
        type: 'delete',
        estimateId: oldRecord.id,
      })
    }
  }

  /**
   * Register a listener for estimate realtime events. Returns an unsubscribe
   * function. Used by useRealTimeEstimates to surface status-change toasts
   * and keep open estimate forms in sync.
   */
  onEstimateEvent(listener: EstimateEventListener): () => void {
    this.estimateEventListeners.add(listener)
    return () => {
      this.estimateEventListeners.delete(listener)
    }
  }

  private emitEstimateEvent(event: EstimateRealtimeEvent): void {
    this.estimateEventListeners.forEach((listener) => {
      try {
        listener(event)
      } catch (err) {
        console.error('[RealtimeManager] Error in estimate event listener:', err)
      }
    })
  }

  /**
   * Setup the always-on invoices subscription. Identical pattern to
   * setupEstimatesSubscription — separate channel so subscription/teardown
   * fail independently and so callers can reason about each entity's
   * connection state on its own.
   */
  private setupInvoicesSubscription(): void {
    if (!this.supabase || !this.currentCompanyId || !this.queryClient) {
      console.error('[RealtimeManager] Cannot setup invoices subscription: missing dependencies')
      return
    }

    if (this.invoicesChannel) {
      this.supabase.removeChannel(this.invoicesChannel)
      this.invoicesChannel = null
    }

    const companyId = this.currentCompanyId
    const queryClient = this.queryClient

    console.log('[RealtimeManager] Setting up invoices subscription for company:', companyId)

    this.invoicesChannel = this.supabase
      .channel(`invoices_${companyId}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'invoices',
        filter: `company_id=eq.${companyId}`
      }, (payload) => {
        console.log('[RealtimeManager] Invoices update received:', payload.eventType)
        this.handleInvoicesUpdate(payload, companyId, queryClient)
      })
      .subscribe((status, err) => {
        this.handleSubscriptionStatus('invoices', status, err)
      })
  }

  /**
   * Handle invoices table updates. Mirror of handleEstimatesUpdate —
   * writes to `['invoices', companyId]` with INSERT-dedupe / UPDATE-merge /
   * DELETE-filter semantics. prevStatus is captured pre-patch from the
   * cached row so we don't depend on REPLICA IDENTITY FULL on the table.
   *
   * NOTE: the realtime payload does NOT include the `client:clients(...)`
   * embed that _getInvoices / _createInvoice / _updateInvoice include.
   * UPDATE merges by spread so an existing client embed survives the patch;
   * a fresh INSERT lands without one and the row gets the embed on the next
   * refetch (the public-view "I paid" flow goes UPDATE, so this rarely
   * matters in practice).
   */
  private handleInvoicesUpdate(
    payload: any,
    companyId: string,
    queryClient: QueryClient
  ): void {
    const { eventType, new: newRecord, old: oldRecord } = payload
    const key = ['invoices', companyId]

    let prevStatus: string | undefined

    queryClient.setQueryData(key, (old: any) => {
      const list: any[] = Array.isArray(old) ? old : []

      switch (eventType) {
        case 'INSERT': {
          if (list.some((e) => e?.id === newRecord.id)) return list
          return [newRecord, ...list]
        }
        case 'UPDATE': {
          const existing = list.find((e) => e?.id === newRecord.id)
          prevStatus = existing?.status
          if (!existing) return [newRecord, ...list]
          return list.map((e) =>
            e?.id === newRecord.id ? { ...e, ...newRecord } : e
          )
        }
        case 'DELETE': {
          return list.filter((e) => e?.id !== oldRecord.id)
        }
        default:
          return list
      }
    })

    if (eventType === 'INSERT') {
      this.emitInvoiceEvent({
        type: 'create',
        invoiceId: newRecord.id,
        newRecord,
      })
    } else if (eventType === 'UPDATE') {
      this.emitInvoiceEvent({
        type: 'update',
        invoiceId: newRecord.id,
        newRecord,
      })
      if (prevStatus !== undefined && prevStatus !== newRecord.status) {
        this.emitInvoiceEvent({
          type: 'status_change',
          invoiceId: newRecord.id,
          oldStatus: prevStatus,
          newStatus: newRecord.status,
          invoice: newRecord,
        })
      }
    } else if (eventType === 'DELETE') {
      this.emitInvoiceEvent({
        type: 'delete',
        invoiceId: oldRecord.id,
      })
    }
  }

  /**
   * Register a listener for invoice realtime events.
   */
  onInvoiceEvent(listener: InvoiceEventListener): () => void {
    this.invoiceEventListeners.add(listener)
    return () => {
      this.invoiceEventListeners.delete(listener)
    }
  }

  private emitInvoiceEvent(event: InvoiceRealtimeEvent): void {
    this.invoiceEventListeners.forEach((listener) => {
      try {
        listener(event)
      } catch (err) {
        console.error('[RealtimeManager] Error in invoice event listener:', err)
      }
    })
  }

  /**
   * Register a listener for job realtime events. Used by useRealTimeJobs to
   * drive status-change toasts (job marked complete, started, etc.) and to
   * react to jobs created by the public estimate-accept flow.
   */
  onJobEvent(listener: JobEventListener): () => void {
    this.jobEventListeners.add(listener)
    return () => {
      this.jobEventListeners.delete(listener)
    }
  }

  private emitJobEvent(event: JobRealtimeEvent): void {
    this.jobEventListeners.forEach((listener) => {
      try {
        listener(event)
      } catch (err) {
        console.error('[RealtimeManager] Error in job event listener:', err)
      }
    })
  }

  /**
   * Subscribe to the current user's user_access_cache row. One channel for
   * the whole app — every component that calls usePermissions reads from
   * the same React Query key, so a single subscription that invalidates
   * ['userAccessCache', userId] keeps every consumer in sync after an
   * admin updates an employee's permissions.
   *
   * Debounced 1s: a single admin save commonly fires multiple postgres
   * changes (employee row + user_access_cache refresh trigger) and we
   * only need one refetch.
   */
  subscribeToUserAccess(userId: string): void {
    if (!this.supabase || !this.queryClient) {
      console.warn('[RealtimeManager] Cannot subscribe to user_access_cache: not initialized')
      return
    }

    // Same user already subscribed: no-op.
    if (this.userAccessChannel && this.userAccessUserId === userId) return

    // Different user (sign-out -> sign-in as someone else): tear down first.
    if (this.userAccessChannel) {
      this.userAccessChannel.unsubscribe()
      this.supabase.removeChannel(this.userAccessChannel)
      this.userAccessChannel = null
    }

    const queryClient = this.queryClient
    this.userAccessUserId = userId

    this.userAccessChannel = this.supabase
      .channel(`user_access_${userId}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'user_access_cache',
        filter: `user_id=eq.${userId}`,
      }, () => {
        if (this.userAccessDebounceTimer) clearTimeout(this.userAccessDebounceTimer)
        this.userAccessDebounceTimer = setTimeout(() => {
          queryClient.invalidateQueries({ queryKey: ['userAccessCache', userId] })
          this.userAccessDebounceTimer = null
        }, 1000)
      })
      .subscribe((status, err) => {
        if (status === 'CHANNEL_ERROR') {
          console.warn('[RealtimeManager] user_access_cache subscription failed:', err)
        }
      })
  }

  /**
   * Subscribe to job details (activity + media) for a specific job
   * Returns an unsubscribe function
   */
  subscribeToJobDetails(jobId: string): () => void {
    if (!this.supabase || !this.queryClient) {
      console.warn('[RealtimeManager] Cannot subscribe to job details: not initialized')
      return () => {}
    }
    
    // Check if already subscribed
    const existing = this.jobDetailsChannels.get(jobId)
    if (existing) {
      console.log('[RealtimeManager] Already subscribed to job details:', jobId)
      return existing.unsubscribe
    }
    
    console.log('[RealtimeManager] Subscribing to job details:', jobId)
    
    const queryClient = this.queryClient
    
    const channel = this.supabase
      .channel(`job_details_${jobId}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'job_activity',
        filter: `job_id=eq.${jobId}`
      }, () => {
        console.log('[RealtimeManager] Job activity update for:', jobId)
        queryClient.invalidateQueries({ queryKey: ['job_activity', jobId] })
      })
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'job_media',
        filter: `job_id=eq.${jobId}`
      }, () => {
        console.log('[RealtimeManager] Job media update for:', jobId)
        queryClient.invalidateQueries({ queryKey: ['job_media', jobId] })
      })
      .subscribe((status, err) => {
        if (status === 'CHANNEL_ERROR') {
          // RLS denial or invalid job_id - handle gracefully
          console.warn(`[RealtimeManager] Job details subscription failed for ${jobId}:`, err)
        }
      })
    
    const unsubscribe = () => {
      console.log('[RealtimeManager] Unsubscribing from job details:', jobId)
      channel.unsubscribe()
      this.supabase?.removeChannel(channel)
      this.jobDetailsChannels.delete(jobId)
    }
    
    this.jobDetailsChannels.set(jobId, { channel, unsubscribe })
    return unsubscribe
  }

  /**
   * Handle subscription status changes
   */
  private handleSubscriptionStatus(
    channelName: string,
    status: string,
    err?: Error
  ): void {
    console.log(`[RealtimeManager] ${channelName} subscription status:`, status)
    
    if (status === 'SUBSCRIBED') {
      this.setConnectionState('connected')
      this.reconnectAttempts = 0
    }
    
    if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
      console.error(`[RealtimeManager] ${channelName} subscription failed:`, err)
      this.setConnectionState('disconnected')
      
      // Retry with exponential backoff
      if (this.reconnectAttempts < this.maxReconnectAttempts) {
        this.reconnectAttempts++
        const delay = this.reconnectBackoffMs * Math.pow(2, this.reconnectAttempts - 1)
        console.log(`[RealtimeManager] Retrying in ${delay}ms (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`)
        
        setTimeout(() => {
          this.reconnect()
        }, delay)
      } else {
        console.error('[RealtimeManager] Max reconnect attempts reached')
      }
    }
    
    if (status === 'CLOSED') {
      this.setConnectionState('disconnected')
    }
  }

  /**
   * Update realtime auth token
   */
  setAuth(token: string): void {
    if (!this.supabase) {
      console.warn('[RealtimeManager] Cannot set auth: not initialized')
      return
    }
    
    console.log('[RealtimeManager] Updating realtime auth token')
    this.supabase.realtime.setAuth(token)
  }

  /**
   * Check connection health
   * Returns true if healthy, false if needs reconnection
   */
  checkHealth(): boolean {
    if (!this.supabase || !this.jobsChannel) {
      return false
    }

    const jobsState = this.jobsChannel.state
    const jobsHealthy = jobsState === 'joined' || jobsState === 'joining'

    // Estimates/invoices channels must also be healthy if we initialized
    // them. If they're null (initialize hasn't run far enough) treat them
    // as healthy so we don't flap reconnects during boot.
    const estimatesState = this.estimatesChannel?.state
    const estimatesHealthy = !this.estimatesChannel
      || estimatesState === 'joined'
      || estimatesState === 'joining'

    const invoicesState = this.invoicesChannel?.state
    const invoicesHealthy = !this.invoicesChannel
      || invoicesState === 'joined'
      || invoicesState === 'joining'

    const isHealthy = jobsHealthy && estimatesHealthy && invoicesHealthy

    console.log(
      '[RealtimeManager] Health check - jobs:', jobsState,
      'estimates:', estimatesState ?? 'none',
      'invoices:', invoicesState ?? 'none',
      'healthy:', isHealthy
    )

    return isHealthy
  }

  /**
   * Force reconnect all subscriptions
   */
  async reconnect(): Promise<void> {
    if (!this.isInitialized || !this.currentCompanyId || !this.supabase) {
      console.warn('[RealtimeManager] Cannot reconnect: not initialized')
      return
    }
    
    console.log('[RealtimeManager] Reconnecting...')
    this.setConnectionState('reconnecting')
    
    // CRITICAL: Refresh the session token before reconnecting to avoid expired JWT errors
    try {
      const { data: { session }, error } = await this.supabase.auth.getSession()
      
      if (error) {
        console.error('[RealtimeManager] Failed to get session:', error)
        // Try to refresh the session explicitly
        const { data: refreshData, error: refreshError } = await this.supabase.auth.refreshSession()
        
        if (refreshError || !refreshData.session) {
          console.error('[RealtimeManager] Failed to refresh session:', refreshError)
          this.setConnectionState('disconnected')
          return
        }
        
        // Update realtime auth with fresh token
        this.supabase.realtime.setAuth(refreshData.session.access_token)
        console.log('[RealtimeManager] Session refreshed, updated realtime auth')
      } else if (session) {
        // Update realtime auth with current token
        this.supabase.realtime.setAuth(session.access_token)
        console.log('[RealtimeManager] Updated realtime auth with current session')
      } else {
        console.warn('[RealtimeManager] No valid session, cannot reconnect')
        this.setConnectionState('disconnected')
        return
      }
    } catch (err) {
      console.error('[RealtimeManager] Error refreshing auth:', err)
      this.setConnectionState('disconnected')
      return
    }
    
    // Tear down existing channels
    await this.tearDownChannels()

    // Rebuild subscriptions
    this.setupJobsSubscription()
    this.setupEstimatesSubscription()
    this.setupInvoicesSubscription()

    // Re-subscribe user_access_cache for the previously-tracked user if any
    const userIdToRebind = this.userAccessUserId
    if (userIdToRebind) {
      // Clear the cached userId so the subscribe path actually rebuilds the
      // channel (the early-return guards on same-user).
      this.userAccessUserId = null
      this.subscribeToUserAccess(userIdToRebind)
    }

    // Re-subscribe to any active job details
    const jobIds = Array.from(this.jobDetailsChannels.keys())
    this.jobDetailsChannels.clear()
    
    for (const jobId of jobIds) {
      this.subscribeToJobDetails(jobId)
    }
  }

  /**
   * Tear down all channels
   */
  private async tearDownChannels(): Promise<void> {
    if (!this.supabase) return
    
    // Cleanup jobs channel
    if (this.jobsChannel) {
      try {
        await this.jobsChannel.unsubscribe()
        this.supabase.removeChannel(this.jobsChannel)
      } catch (err) {
        console.warn('[RealtimeManager] Error cleaning up jobs channel:', err)
      }
      this.jobsChannel = null
    }

    // Cleanup estimates channel
    if (this.estimatesChannel) {
      try {
        await this.estimatesChannel.unsubscribe()
        this.supabase.removeChannel(this.estimatesChannel)
      } catch (err) {
        console.warn('[RealtimeManager] Error cleaning up estimates channel:', err)
      }
      this.estimatesChannel = null
    }

    // Cleanup invoices channel
    if (this.invoicesChannel) {
      try {
        await this.invoicesChannel.unsubscribe()
        this.supabase.removeChannel(this.invoicesChannel)
      } catch (err) {
        console.warn('[RealtimeManager] Error cleaning up invoices channel:', err)
      }
      this.invoicesChannel = null
    }

    // Cleanup user_access_cache channel
    if (this.userAccessChannel) {
      try {
        await this.userAccessChannel.unsubscribe()
        this.supabase.removeChannel(this.userAccessChannel)
      } catch (err) {
        console.warn('[RealtimeManager] Error cleaning up user_access channel:', err)
      }
      this.userAccessChannel = null
      this.userAccessUserId = null
      if (this.userAccessDebounceTimer) {
        clearTimeout(this.userAccessDebounceTimer)
        this.userAccessDebounceTimer = null
      }
    }

    // Cleanup job details channels
    for (const [jobId, { channel }] of this.jobDetailsChannels) {
      try {
        await channel.unsubscribe()
        this.supabase.removeChannel(channel)
      } catch (err) {
        console.warn(`[RealtimeManager] Error cleaning up job details channel ${jobId}:`, err)
      }
    }
    this.jobDetailsChannels.clear()
  }

  /**
   * Destroy the manager and cleanup all subscriptions
   */
  async destroy(): Promise<void> {
    console.log('[RealtimeManager] Destroying...')
    
    // Cleanup lifecycle listeners
    this.cleanupLifecycleListeners()
    
    // Tear down all channels
    await this.tearDownChannels()
    
    // Reset state. Estimate event listeners are intentionally kept so
    // components that registered during boot continue to receive events
    // when the manager re-initializes on the next auth cycle. They unhook
    // themselves via the returned cleanup function in their useEffect.
    this.isInitialized = false
    this.currentCompanyId = null
    this.queryClient = null
    this.reconnectAttempts = 0
    this.setConnectionState('disconnected')
    
    console.log('[RealtimeManager] Destroyed')
  }

  /**
   * Set connection state and notify listeners
   */
  private setConnectionState(state: ConnectionState): void {
    if (this.connectionState === state) return
    
    console.log('[RealtimeManager] Connection state:', this.connectionState, '->', state)
    this.connectionState = state
    
    // Notify all listeners
    this.stateChangeListeners.forEach(callback => {
      try {
        callback(state)
      } catch (err) {
        console.error('[RealtimeManager] Error in state change listener:', err)
      }
    })
  }

  /**
   * Register a state change listener
   * Returns an unsubscribe function
   */
  onStateChange(callback: StateChangeCallback): () => void {
    this.stateChangeListeners.add(callback)
    
    // Immediately call with current state
    callback(this.connectionState)
    
    return () => {
      this.stateChangeListeners.delete(callback)
    }
  }

  /**
   * Get current connection state
   */
  getConnectionState(): ConnectionState {
    return this.connectionState
  }

  /**
   * Get current company ID
   */
  getCurrentCompanyId(): string | null {
    return this.currentCompanyId
  }

  /**
   * Check if manager is initialized
   */
  getIsInitialized(): boolean {
    return this.isInitialized
  }
}

// Export singleton instance
export const RealtimeManager = new RealtimeManagerClass()
export default RealtimeManager

